"use client"

import React, { ReactNode, useState } from "react"
import {
  PieChart as PieChartComponent,
  Pie,
  Sector,
  ResponsiveContainer,
  Legend,
} from "recharts"
import type { PieSectorDataItem } from "recharts/types/polar/Pie"
import type { ActiveShape } from "recharts/types/util/types"
import "./style.scss"

type DataItem = {
  name: string
  value: number
  fill: string
  icon?: ReactNode
}

const ICON_SIZE = 20 // Size of the icon container

const renderActiveShape = (props: Record<string, unknown>) => {
  const cx = props.cx as number
  const cy = props.cy as number
  const innerRadius = props.innerRadius as number
  const outerRadius = props.outerRadius as number
  const startAngle = props.startAngle as number
  const endAngle = props.endAngle as number
  const fill = props.fill as string
  const payload = props.payload as DataItem
  const percent = props.percent as number
  const value = props.value as number

  return (
    <g>
      <text
        x={cx}
        y={cy - 5}
        textAnchor="middle"
        fill={fill}
        className="text-lg font-bold transition-all duration-300 ease-out animate-fadeIn"
      >
        {payload.name}
      </text>
      <text
        x={cx}
        y={cy + 15}
        textAnchor="middle"
        fill={fill}
        className="text-sm transition-all duration-300 ease-out animate-fadeIn delay-150"
      >
        {`Value ${value}`} {`(${(percent * 100).toFixed(0)}%)`}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        className="transition-all duration-300 ease-out"
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
        className="transition-all duration-300 ease-out"
      />
    </g>
  )
}

interface PieChartProps {
  data: DataItem[]
}

interface PieLabelRenderProps {
  cx: number
  cy: number
  midAngle: number
  innerRadius: number
  outerRadius: number
  percent: number
  payload: DataItem
}

const PieChart: React.FC<PieChartProps> = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(-1)

  const onPieEnter = (_: unknown, index: number) => {
    setActiveIndex(index)
  }

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    payload,
  }: PieLabelRenderProps) => {
    const RADIAN = Math.PI / 180
    const x =
      cx + (innerRadius + outerRadius / 4) * Math.cos(-midAngle * RADIAN)
    const y =
      cy + (innerRadius + outerRadius / 4) * Math.sin(-midAngle * RADIAN)
    const percentValue = (percent ?? 0) * 100

    // Only show label if percentage is above 5%

    if (percentValue < 5) {
      return null
    }

    // If icon is provided and percentage is above 5%, render icon
    if (payload?.icon) {
      return (
        <foreignObject
          x={x - ICON_SIZE / 2}
          y={y - ICON_SIZE / 2}
          width={ICON_SIZE}
          height={ICON_SIZE}
        >
          {payload.icon}
        </foreignObject>
      )
    }
  }

  return (
    <ResponsiveContainer height="90%">
      <PieChartComponent>
        <Pie
          activeIndex={activeIndex}
          activeShape={
            renderActiveShape as unknown as ActiveShape<PieSectorDataItem>
          }
          data={data}
          cx="50%"
          cy="50%"
          innerRadius="45%"
          width={220}
          height={220}
          outerRadius="90%"
          dataKey="value"
          onMouseEnter={onPieEnter}
          onMouseLeave={() => setActiveIndex(-1)}
          strokeWidth={0}
          label={renderCustomizedLabel}
        />
        <Legend
          layout="horizontal"
          align="left"
          verticalAlign="bottom"
          iconType="plainline"
          iconSize={0}
          height={50}
          formatter={(
            _: unknown,
            entry: {
              color?: string
              payload?: {
                strokeDasharray: React.ReactText
                value?: unknown
                name?: string
              }
            },
          ) => (
            <div className="flex flex-row items-center justify-center gap-system-sm">
              <div
                className="w-system-xxs h-system-md rounded-system-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-white text-sm">
                {entry.payload?.name}: {entry.payload?.value as React.ReactText}
                %
              </span>
            </div>
          )}
        />
      </PieChartComponent>
    </ResponsiveContainer>
  )
}
export default PieChart
